(ns quuppa.qsm.planner.tree
  (:require [re-frame.core :as rf]
            [reagent.core :as r]
            [quuppa.qsm.planner.subs :as su]
            [quuppa.qsm.planner.utils :as utils]
            ["rc-tree" :as RcTree]))

(defn imdf->tree-data
  "Converts IMDF venue data to rc-tree format.
   Each node has: {:key id :title name :children [...]}
   Adds explicit container nodes for 'Levels', 'Background images', and 'Simulation paths'."
  [imdf-data]
  (when-let [venue (:venue imdf-data)]
    (let [buildings (:buildings venue)]
      (for [[building-id building] buildings]
        {:key (str "building-" building-id)
         :title (or (utils/localized-label (:name building)) (str "Building " (subs (str building-id) 0 8)))
         :dataType "building"
         :dataId building-id
         :children [{:key (str "levels-" building-id)
                     :title "Levels"
                     :dataType "levels-container"
                     :dataBuildingId building-id
                     :children (for [[level-id level] (:levels building)]
                                 {:key (str "level-" level-id)
                                  :title (or (utils/localized-label (:name level)) (str "Level " (subs (str level-id) 0 8)))
                                  :dataType "level"
                                  :dataId level-id
                                  :dataBuildingId building-id
                                  :children [{:key (str "backgrounds-" level-id)
                                              :title "Background images"
                                              :dataType "backgrounds-container"
                                              :dataLevelId level-id
                                              :dataBuildingId building-id
                                              :children (for [[bg-id bg] (:siteresource-backgroundimages level)]
                                                          {:key (str "background-" bg-id)
                                                           :title (or (utils/localized-label (:image-url bg)) (str "Background " (subs (str bg-id) 0 8)))
                                                           :dataType "background"
                                                           :dataId bg-id
                                                           :dataLevelId level-id
                                                           :dataBuildingId building-id})}
                                             {:key (str "routes-" level-id)
                                              :title "Simulation paths"
                                              :dataType "routes-container"
                                              :dataLevelId level-id
                                              :dataBuildingId building-id
                                              :children (for [[route-id route] (:routes level)]
                                                          {:key (str "route-" route-id)
                                                           :title (or (utils/localized-label (:name route)) (str "Path " (subs (str route-id) 0 8)))
                                                           :dataType "route"
                                                           :dataId route-id
                                                           :dataLevelId level-id
                                                           :dataBuildingId building-id})}
                                             {:key (str "geofences-" level-id)
                                              :title "Geofences"
                                              :dataType "geofences-container"
                                              :dataLevelId level-id
                                              :dataBuildingId building-id
                                              :children (for [[geofence-id geofence] (:geofences level)]
                                                          {:key (str "geofence-" geofence-id)
                                                           :title (or (utils/localized-label (:name geofence)) (str "Geofence " (subs (str geofence-id) 0 8)))
                                                           :dataType "geofence"
                                                           :dataId geofence-id
                                                           :dataLevelId level-id
                                                           :dataBuildingId building-id})}
                                             {:key (str "units-" level-id)
                                              :title "Units"
                                              :dataType "units-container"
                                              :dataLevelId level-id
                                              :dataBuildingId building-id
                                              :children (for [[unit-id unit] (:units level)]
                                                          {:key (str "unit-" unit-id)
                                                           :title (or (utils/localized-label (:name unit)) (str "Unit " (subs (str unit-id) 0 8)))
                                                           :dataType "unit"
                                                           :dataId unit-id
                                                           :dataLevelId level-id
                                                           :dataBuildingId building-id})}
                                             {:key (str "openings-" level-id)
                                              :title "Openings"
                                              :dataType "openings-container"
                                              :dataLevelId level-id
                                              :dataBuildingId building-id
                                              :children (for [[opening-id opening] (:openings level)]
                                                          {:key (str "opening-" opening-id)
                                                           :title (or (utils/localized-label (:name opening)) (str "Opening " (subs (str opening-id) 0 8)))
                                                           :dataType "opening"
                                                           :dataId opening-id
                                                           :dataLevelId level-id
                                                           :dataBuildingId building-id})}
                                             {:key (str "locators-" level-id)
                                              :title "Locators"
                                              :dataType "locators-container"
                                              :dataLevelId level-id
                                              :dataBuildingId building-id
                                              :children (for [[locator-id locator] (:locators level)]
                                                          {:key (str "locator-" locator-id)
                                                           :title (or (utils/localized-label (:name locator)) (str "Locator " (subs (str locator-id) 0 8)))
                                                           :dataType "locator"
                                                           :dataId locator-id
                                                           :dataLevelId level-id
                                                           :dataBuildingId building-id})}]})}]}))))

(defn imdf-tree [_plan-id _on-select _selected-key]
  (let [;; r/atom tracks controlled expanded keys; nil = not yet initialized
        expanded-state (r/atom nil)]
    (fn [plan-id on-select selected-key]
      (let [plan @(rf/subscribe [::su/site-plan plan-id])
            building-id @(rf/subscribe [:nav/match :parameters :query :building-id])
            level-id @(rf/subscribe [:nav/match :parameters :query :level-id])
            route-id @(rf/subscribe [:nav/match :parameters :query :route-id])
            background-id @(rf/subscribe [:nav/match :parameters :query :background-id])
            tree-data (imdf->tree-data (:imdf plan))
            ;; Build required expanded keys from URL params
            ;; Treat "null" string (from bad URL state) as nil
            route-id-clean (when (and route-id (not= route-id "null")) route-id)
            background-id-clean (when (and background-id (not= background-id "null")) background-id)
            expanded-keys (cond-> []
                                  building-id (conj (str "building-" building-id)
                                                    (str "levels-" building-id))
                                  level-id (conj (str "level-" level-id)
                                                 (str "backgrounds-" level-id)
                                                 (str "routes-" level-id)
                                                 (str "geofences-" level-id)
                                                 (str "units-" level-id)
                                                 (str "openings-" level-id)
                                                 (str "locators-" level-id))
                                  background-id-clean (conj (str "background-" background-id-clean)))
            ;; Prioritise what the user clicked (selected-key from selected-node) so that
            ;; container nodes ("backgrounds-container" etc.) can also appear highlighted.
            ;; Fall back to URL params to restore selection on page load / direct navigation.
            effective-selected-key (or selected-key
                                       (when route-id-clean (str "route-" route-id-clean))
                                       (when background-id-clean (str "background-" background-id-clean)))
            ;; Force realization of lazy seq to catch any errors
            tree-data-vec (try
                            (vec tree-data)
                            (catch :default e
                              (js/console.error "Error realizing tree-data:" e)
                              []))
            ;; Initialize expanded-state once when tree data first arrives (async plan load).
            ;; Using controlled expandedKeys instead of defaultExpandedKeys ensures
            ;; the tree expands correctly even after the initial empty render.
            _ (when (and (nil? @expanded-state) (seq tree-data-vec))
                (reset! expanded-state (set expanded-keys)))
            current-expanded (or @expanded-state (set expanded-keys))]
        [:div.imdf-tree-wrapper
         ^{:key (str "tree-" level-id)}
         [:> (.-default RcTree)
          {:treeData (clj->js tree-data-vec)
           :expandedKeys (clj->js (vec current-expanded))
           :onExpand (fn [keys]
                       (reset! expanded-state (set (js->clj keys))))
           :selectedKeys (clj->js (if effective-selected-key [effective-selected-key] []))
           :onSelect (fn [selected-keys info]
                       (let [^js node (.-node info)
                             data-type (.-dataType node)
                             data-id (.-dataId node)
                             data-building-id (.-dataBuildingId node)
                             data-level-id (.-dataLevelId node)
                             node-key (first selected-keys)]
                         ;; Call the on-select callback with selection info
                         (on-select {:key node-key
                                     :data-type data-type
                                     :data-id data-id
                                     :building-id data-building-id
                                     :level-id data-level-id})
                         ;; Update URL based on selection
                         ;; Use function form with dissoc to properly remove route-id (merge with nil causes "null" in URL)
                         (case data-type
                           "building" (rf/dispatch [:nav/update-query
                                                    (fn [q] (-> q
                                                                (assoc :building-id data-id)
                                                                (dissoc :level-id :route-id :geofence-id :unit-id :opening-id :locator-id)))])
                           "levels-container" (rf/dispatch [:nav/update-query
                                                            (fn [q] (-> q
                                                                        (assoc :building-id data-building-id)
                                                                        (dissoc :level-id :route-id :geofence-id :unit-id :opening-id :locator-id)))])
                           "level" (rf/dispatch [:nav/update-query
                                                 (fn [q] (-> q
                                                             (assoc :building-id data-building-id :level-id data-id)
                                                             (dissoc :route-id :geofence-id :unit-id :opening-id :locator-id)))])
                           "backgrounds-container" (rf/dispatch [:nav/update-query
                                                                 (fn [q] (-> q
                                                                             (assoc :building-id data-building-id :level-id data-level-id)
                                                                             (dissoc :route-id :geofence-id :unit-id :opening-id :locator-id)))])
                           "background" (rf/dispatch [:nav/update-query
                                                      (fn [q] (-> q
                                                                  (assoc :building-id data-building-id :level-id data-level-id :background-id data-id)
                                                                  (dissoc :route-id :geofence-id :unit-id :opening-id :locator-id)))])
                           "routes-container" (rf/dispatch [:nav/update-query
                                                            (fn [q] (-> q
                                                                        (assoc :building-id data-building-id :level-id data-level-id)
                                                                        (dissoc :route-id :geofence-id :unit-id :opening-id :locator-id)))])
                           "geofences-container" (rf/dispatch [:nav/update-query
                                                               (fn [q] (-> q
                                                                           (assoc :building-id data-building-id :level-id data-level-id)
                                                                           (dissoc :route-id :geofence-id :unit-id :opening-id :locator-id)))])
                           "units-container" (rf/dispatch [:nav/update-query
                                                           (fn [q] (-> q
                                                                       (assoc :building-id data-building-id :level-id data-level-id)
                                                                       (dissoc :route-id :geofence-id :unit-id :opening-id :locator-id)))])
                           "openings-container" (rf/dispatch [:nav/update-query
                                                              (fn [q] (-> q
                                                                          (assoc :building-id data-building-id :level-id data-level-id)
                                                                          (dissoc :route-id :geofence-id :unit-id :opening-id :locator-id)))])
                           "locators-container" (rf/dispatch [:nav/update-query
                                                              (fn [q] (-> q
                                                                          (assoc :building-id data-building-id :level-id data-level-id)
                                                                          (dissoc :route-id :geofence-id :unit-id :opening-id :locator-id)))])
                           "route" (rf/dispatch [:nav/update-query
                                                 (fn [q] (-> q
                                                             (assoc :building-id data-building-id :level-id data-level-id :route-id data-id)
                                                             (dissoc :geofence-id :unit-id :opening-id :locator-id)))])
                           "geofence" (rf/dispatch [:nav/update-query
                                                    (fn [q] (-> q
                                                                (assoc :building-id data-building-id :level-id data-level-id :geofence-id data-id)
                                                                (dissoc :route-id :unit-id :opening-id :locator-id)))])
                           "unit" (rf/dispatch [:nav/update-query
                                                (fn [q] (-> q
                                                            (assoc :building-id data-building-id :level-id data-level-id :unit-id data-id)
                                                            (dissoc :route-id :geofence-id :opening-id :locator-id)))])
                           "opening" (rf/dispatch [:nav/update-query
                                                   (fn [q] (-> q
                                                               (assoc :building-id data-building-id :level-id data-level-id :opening-id data-id)
                                                               (dissoc :route-id :geofence-id :unit-id :locator-id)))])
                           "locator" (rf/dispatch [:nav/update-query
                                                   (fn [q] (-> q
                                                               (assoc :building-id data-building-id :level-id data-level-id :locator-id data-id)
                                                               (dissoc :route-id :geofence-id :unit-id :opening-id)))])
                           nil)))}]]))))

